// Pure math for the vidamor multiplayer canvas (VidamorCanvas.svelte). No DOM,
// no Convex — transforms and stroke sampling in, transforms and points out —
// so it is unit-tested in isolation (tests/unit/vidamorCanvasCore.test.ts).
//
// The canvas is a fixed-size WORLD drawn inside a viewport. The viewport shows
// the world through a transform {scale, ox, oy}: screen = world * scale +
// offset. One finger (or the mouse) paints; two fingers pinch-zoom and pan;
// the wheel zooms around the cursor. Every gesture goes through
// clampTransform so the world never drifts off-screen and the zoom stays
// within [fit, MAX_SCALE].

/** World size in canvas units. Big enough for a flock, small enough to find each other. */
export const WORLD_WIDTH = 2400;
export const WORLD_HEIGHT = 1600;

/** Hard zoom-in ceiling (fit-to-viewport is the floor, computed per viewport). */
export const MAX_SCALE = 8;

/** A stroke is a flat [x0,y0,x1,y1,...] array; capped so a single stroke can't
 *  balloon a document. 600 numbers = 300 sampled points ≈ a very long line. */
export const MAX_STROKE_NUMBERS = 600;

/** Minimum distance (world units) between sampled points — straight segments
 *  collapse, wiggles survive. */
export const MIN_SAMPLE_DISTANCE = 2;

/** The vidamor paint palette: page inks first, then two guest colors so the
 *  drawings aren't monochrome rust. Cream is the "eraser". */
export const CANVAS_PALETTE: readonly string[] = [
  '#3a2c22', // ink
  '#a64b2e', // rust
  '#dd8140', // deep orange
  '#f0a566', // light orange
  '#d0492b', // soldout red
  '#4a6b52', // moss (guest)
  '#3d5a80', // night blue (guest)
  '#fbf0e1' // cream — draws "nothing", i.e. erases
] as const;

export const BRUSH_SIZES: readonly number[] = [4, 9, 18] as const;

export interface CanvasTransform {
  scale: number;
  ox: number;
  oy: number;
}

export interface Point {
  x: number;
  y: number;
}

/** The scale at which the whole world exactly fits the viewport (zoom floor). */
export function fitScale(viewportWidth: number, viewportHeight: number): number {
  if (viewportWidth <= 0 || viewportHeight <= 0) return 1;
  return Math.min(viewportWidth / WORLD_WIDTH, viewportHeight / WORLD_HEIGHT);
}

export function screenToWorld(t: CanvasTransform, sx: number, sy: number): Point {
  return { x: (sx - t.ox) / t.scale, y: (sy - t.oy) / t.scale };
}

/**
 * Clamp a transform so the zoom stays within [fit, MAX_SCALE] and the world
 * never leaves a gap at a viewport edge; axes where the world is smaller than
 * the viewport are centered instead.
 */
export function clampTransform(
  t: CanvasTransform,
  viewportWidth: number,
  viewportHeight: number
): CanvasTransform {
  const minScale = fitScale(viewportWidth, viewportHeight);
  const scale = Math.min(Math.max(t.scale, minScale), MAX_SCALE);
  const worldW = WORLD_WIDTH * scale;
  const worldH = WORLD_HEIGHT * scale;
  const clampAxis = (offset: number, world: number, viewport: number) => {
    if (world <= viewport) return (viewport - world) / 2;
    return Math.min(0, Math.max(viewport - world, offset));
  };
  return {
    scale,
    ox: clampAxis(t.ox, worldW, viewportWidth),
    oy: clampAxis(t.oy, worldH, viewportHeight)
  };
}

/** Zoom by `factor` keeping the screen point (cx, cy) anchored. */
export function zoomAt(
  t: CanvasTransform,
  factor: number,
  cx: number,
  cy: number,
  viewportWidth: number,
  viewportHeight: number
): CanvasTransform {
  const minScale = fitScale(viewportWidth, viewportHeight);
  const scale = Math.min(Math.max(t.scale * factor, minScale), MAX_SCALE);
  const applied = scale / t.scale;
  return clampTransform(
    {
      scale,
      ox: cx - (cx - t.ox) * applied,
      oy: cy - (cy - t.oy) * applied
    },
    viewportWidth,
    viewportHeight
  );
}

/** Pan by a screen-space delta. */
export function panBy(
  t: CanvasTransform,
  dx: number,
  dy: number,
  viewportWidth: number,
  viewportHeight: number
): CanvasTransform {
  return clampTransform({ scale: t.scale, ox: t.ox + dx, oy: t.oy + dy }, viewportWidth, viewportHeight);
}

/**
 * Two-finger gesture: previous and current positions of both pointers become
 * one zoom (distance ratio, anchored at the previous midpoint) plus one pan
 * (midpoint travel).
 */
export function pinchTransform(
  t: CanvasTransform,
  prevA: Point,
  prevB: Point,
  nextA: Point,
  nextB: Point,
  viewportWidth: number,
  viewportHeight: number
): CanvasTransform {
  const prevDist = Math.hypot(prevB.x - prevA.x, prevB.y - prevA.y);
  const nextDist = Math.hypot(nextB.x - nextA.x, nextB.y - nextA.y);
  const factor = prevDist > 0 ? nextDist / prevDist : 1;
  const prevMid = { x: (prevA.x + prevB.x) / 2, y: (prevA.y + prevB.y) / 2 };
  const nextMid = { x: (nextA.x + nextB.x) / 2, y: (nextA.y + nextB.y) / 2 };
  const zoomed = zoomAt(t, factor, prevMid.x, prevMid.y, viewportWidth, viewportHeight);
  return panBy(zoomed, nextMid.x - prevMid.x, nextMid.y - prevMid.y, viewportWidth, viewportHeight);
}

/** The transform that centers the world at fit zoom — the opening view. */
export function initialTransform(viewportWidth: number, viewportHeight: number): CanvasTransform {
  return clampTransform({ scale: fitScale(viewportWidth, viewportHeight), ox: 0, oy: 0 }, viewportWidth, viewportHeight);
}

/**
 * Append a world point to an in-progress stroke (flat [x,y,...] array,
 * mutated). Skips points closer than MIN_SAMPLE_DISTANCE to the previous one
 * and refuses to grow past MAX_STROKE_NUMBERS. Returns false when the stroke
 * is full — the caller should finish it and start a new one.
 */
export function appendStrokePoint(points: number[], x: number, y: number): boolean {
  if (points.length >= MAX_STROKE_NUMBERS) return false;
  const px = clampToWorld(x, WORLD_WIDTH);
  const py = clampToWorld(y, WORLD_HEIGHT);
  const n = points.length;
  if (n >= 2) {
    const dx = px - points[n - 2];
    const dy = py - points[n - 1];
    if (Math.hypot(dx, dy) < MIN_SAMPLE_DISTANCE) return true; // kept, just not stored
  }
  points.push(px, py);
  return true;
}

function clampToWorld(value: number, extent: number): number {
  return Math.min(Math.max(Math.round(value * 10) / 10, 0), extent);
}

// --- replay ------------------------------------------------------------------
// Strokes carry createdAt (order) and durationMs (how long they took). The
// replay plays them back in order, each animating point-by-point over its
// recorded duration, with the dead time between strokes compressed to a small
// beat. The whole timeline is scaled down to fit a watchable target length.

/** Aim for a replay of roughly this length; long nights get compressed. */
export const REPLAY_TARGET_MS = 25000;
/** Pause between strokes (pre-compression). */
export const REPLAY_GAP_MS = 90;
/** Per-stroke animation bounds (pre-compression). */
export const REPLAY_MIN_STROKE_MS = 60;
export const REPLAY_MAX_STROKE_MS = 1500;

export interface ReplayStroke {
  points: number[];
  durationMs?: number;
}

export interface ReplayStep {
  /** Timeline offset where this stroke starts animating. */
  start: number;
  /** How long this stroke animates. */
  duration: number;
}

export interface ReplayPlan {
  steps: ReplayStep[];
  total: number;
}

/** Estimated draw time for strokes recorded before durationMs existed. */
export function estimateStrokeDuration(stroke: ReplayStroke): number {
  if (typeof stroke.durationMs === 'number' && stroke.durationMs > 0) return stroke.durationMs;
  return (stroke.points.length / 2) * 30; // ~30ms per sampled point
}

/**
 * Build the replay timeline: one step per stroke, in the given (createdAt)
 * order. Durations are clamped to [REPLAY_MIN, REPLAY_MAX] then the whole
 * timeline is scaled so it never exceeds targetMs.
 */
export function replayPlan(strokes: ReplayStroke[], targetMs: number = REPLAY_TARGET_MS): ReplayPlan {
  const raw: number[] = strokes.map(stroke =>
    Math.min(Math.max(estimateStrokeDuration(stroke), REPLAY_MIN_STROKE_MS), REPLAY_MAX_STROKE_MS)
  );
  const rawTotal = raw.reduce((sum, d) => sum + d, 0) + REPLAY_GAP_MS * Math.max(0, strokes.length - 1);
  const scale = rawTotal > targetMs && rawTotal > 0 ? targetMs / rawTotal : 1;
  const steps: ReplayStep[] = [];
  let cursor = 0;
  for (const duration of raw) {
    const scaled = duration * scale;
    steps.push({ start: cursor, duration: scaled });
    cursor += scaled + REPLAY_GAP_MS * scale;
  }
  const total = steps.length > 0 ? steps[steps.length - 1].start + steps[steps.length - 1].duration : 0;
  return { steps, total };
}

export interface ReplayProgress {
  /** Strokes fully drawn at this point of the timeline. */
  complete: number;
  /** Fraction (0..1) of the currently-animating stroke; null between/after strokes. */
  fraction: number | null;
  done: boolean;
}

/** Where the replay is at `elapsed` ms into the plan's timeline. */
export function replayProgress(plan: ReplayPlan, elapsed: number): ReplayProgress {
  if (plan.steps.length === 0 || elapsed >= plan.total) {
    return { complete: plan.steps.length, fraction: null, done: true };
  }
  let complete = 0;
  for (const step of plan.steps) {
    if (elapsed >= step.start + step.duration) {
      complete++;
      continue;
    }
    if (elapsed >= step.start) {
      const fraction = step.duration > 0 ? (elapsed - step.start) / step.duration : 1;
      return { complete, fraction, done: false };
    }
    break; // in the gap before this step
  }
  return { complete, fraction: null, done: false };
}
